import React, { useEffect, useState } from 'react';

import { PREFERENCES_COPY, PRIVACY_COOKIES_HREF } from '../../constants/cookieConsentCopy';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useDialogElement } from '../../hooks/useDialogElement';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, GRADIENTS, RADII, STROKES } from '../../styles/tokens';

const cardStyle: React.CSSProperties = {
  background: FILLS.surfaceRaised,
  border: `1px solid ${STROKES.subtle}`,
  borderRadius: RADII.control,
  padding: '14px 16px',
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const cardLabelStyle: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: '15px',
  fontWeight: 600,
  color: FILLS.textPrimary,
};

const descriptionStyle: React.CSSProperties = {
  margin: '8px 0 0',
  fontFamily: FONTS.body,
  fontSize: '13px',
  lineHeight: 1.5,
  color: FILLS.textMuted,
};

const detailsStyle: React.CSSProperties = {
  marginTop: '8px',
  fontFamily: FONTS.body,
  fontSize: '12px',
  color: FILLS.textMuted,
};

const secondaryButtonStyle: React.CSSProperties = {
  height: '40px',
  background: FILLS.input,
  border: `1px solid ${STROKES.elevated}`,
  borderRadius: RADII.control,
  color: FILLS.textPrimary,
  fontFamily: FONTS.body,
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

const SWITCH_TRACK_WIDTH = 40;
const SWITCH_THUMB_SIZE = 18;
const SWITCH_INSET = 3;

const AnalyticsSwitch: React.FC<{ checked: boolean; onToggle: () => void }> = ({
  checked,
  onToggle,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={PREFERENCES_COPY.analyticsLabel}
    onClick={onToggle}
    style={{
      position: 'relative',
      width: `${SWITCH_TRACK_WIDTH}px`,
      height: `${SWITCH_THUMB_SIZE + 2 * SWITCH_INSET}px`,
      flexShrink: 0,
      padding: 0,
      border: `1px solid ${checked ? STROKES.gold : STROKES.elevated}`,
      borderRadius: RADII.pill,
      background: checked ? FILLS.gold : FILLS.input,
      cursor: 'pointer',
    }}
  >
    <span
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: `${SWITCH_INSET - 1}px`,
        left: checked
          ? `${SWITCH_TRACK_WIDTH - SWITCH_THUMB_SIZE - SWITCH_INSET - 1}px`
          : `${SWITCH_INSET - 1}px`,
        width: `${SWITCH_THUMB_SIZE}px`,
        height: `${SWITCH_THUMB_SIZE}px`,
        borderRadius: RADII.circle,
        background: checked ? FILLS.page : FILLS.textMuted,
        transition: 'left 0.15s ease',
      }}
    />
  </button>
);

/**
 * Privacy choices dialog, mounted once in `Layout`. Opened from the footer
 * links and the consent banner's Manage button through the consent provider; a native
 * `<dialog>` so it sits in the top layer and the browser restores focus to the
 * opener on close.
 */
const CookiePreferencesPanel: React.FC = () => {
  const {
    analyticsAllowed,
    globalPrivacyControlDetected,
    isPreferencesOpen,
    closePreferences,
    acceptAnalytics,
    rejectAnalytics,
    saveChoices,
  } = useCookieConsent();
  const { dialogRef, openDialog, closeDialog } = useDialogElement();
  const isMobile = useIsMobile();
  const [analyticsDraft, setAnalyticsDraft] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isPreferencesOpen && !dialog.open) {
      setAnalyticsDraft(analyticsAllowed);
      openDialog();
    } else if (!isPreferencesOpen && dialog.open) {
      closeDialog();
    }
    // The draft is seeded only on the closed-to-open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreferencesOpen, dialogRef, openDialog, closeDialog]);

  return (
    <dialog
      ref={dialogRef as React.RefObject<HTMLDialogElement>}
      aria-labelledby="cookie-preferences-title"
      onClose={closePreferences}
      onClick={(event) => {
        if (event.target === event.currentTarget) closePreferences();
      }}
      style={{
        width: isMobile ? '100%' : 'calc(100% - 32px)',
        maxWidth: isMobile ? '100%' : '520px',
        maxHeight: isMobile ? '88dvh' : 'calc(100dvh - 48px)',
        margin: isMobile ? 'auto 0 0' : 'auto',
        overflowY: 'auto',
        background: FILLS.surface,
        border: `1px solid ${STROKES.elevated}`,
        borderRadius: isMobile ? `${RADII.sheet} ${RADII.sheet} 0 0` : RADII.card,
      }}
    >
      <div
        style={{
          padding: isMobile ? '20px 16px calc(24px + env(safe-area-inset-bottom))' : '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <h2
            id="cookie-preferences-title"
            style={{
              margin: 0,
              fontFamily: FONTS.heading,
              fontSize: '22px',
              fontWeight: 600,
              color: FILLS.textPrimary,
            }}
          >
            {PREFERENCES_COPY.title}
          </h2>
          <button
            type="button"
            aria-label={PREFERENCES_COPY.close}
            onClick={closePreferences}
            style={{
              width: '32px',
              height: '32px',
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              color: FILLS.textMuted,
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <p
          style={{
            margin: 0,
            fontFamily: FONTS.body,
            fontSize: '14px',
            lineHeight: 1.5,
            color: FILLS.textMuted,
          }}
        >
          {PREFERENCES_COPY.lede}
        </p>

        {globalPrivacyControlDetected && (
          <div
            style={{
              background: FILLS.successBg,
              border: `1px solid ${STROKES.success}`,
              borderRadius: RADII.control,
              padding: '12px 14px',
              fontFamily: FONTS.body,
              fontSize: '13px',
              lineHeight: 1.5,
              color: FILLS.textLight,
            }}
          >
            <strong style={{ display: 'block', color: FILLS.success }}>
              {PREFERENCES_COPY.gpcHeading}
            </strong>
            {PREFERENCES_COPY.gpcBody}
          </div>
        )}

        <section style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardLabelStyle}>{PREFERENCES_COPY.necessaryLabel}</span>
            <span
              style={{
                fontFamily: FONTS.body,
                fontSize: '12px',
                fontWeight: 600,
                color: FILLS.success,
              }}
            >
              {PREFERENCES_COPY.necessaryStatus}
            </span>
          </div>
          <p style={descriptionStyle}>{PREFERENCES_COPY.necessaryDescription}</p>
          <details style={detailsStyle}>
            <summary style={{ cursor: 'pointer' }}>{PREFERENCES_COPY.detailsSummary}</summary>
            <p style={{ margin: '6px 0 0' }}>{PREFERENCES_COPY.necessaryStored}</p>
          </details>
        </section>

        <section style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardLabelStyle}>{PREFERENCES_COPY.analyticsLabel}</span>
            <AnalyticsSwitch
              checked={analyticsDraft}
              onToggle={() => setAnalyticsDraft((current) => !current)}
            />
          </div>
          <p style={descriptionStyle}>{PREFERENCES_COPY.analyticsDescription}</p>
          <details style={detailsStyle}>
            <summary style={{ cursor: 'pointer' }}>{PREFERENCES_COPY.detailsSummary}</summary>
            <p style={{ margin: '6px 0 0' }}>{PREFERENCES_COPY.analyticsStored}</p>
          </details>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={() => {
              rejectAnalytics();
              closePreferences();
            }}
          >
            {PREFERENCES_COPY.rejectAll}
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={() => {
              acceptAnalytics();
              closePreferences();
            }}
          >
            {PREFERENCES_COPY.acceptAll}
          </button>
          <button
            type="button"
            style={{
              ...secondaryButtonStyle,
              gridColumn: '1 / -1',
              background: GRADIENTS.goldButton,
              border: 'none',
              color: FILLS.page,
            }}
            onClick={() => {
              saveChoices({ analytics: analyticsDraft });
              closePreferences();
            }}
          >
            {PREFERENCES_COPY.saveChoices}
          </button>
        </div>

        <p
          style={{
            margin: 0,
            fontFamily: FONTS.body,
            fontSize: '12px',
            lineHeight: 1.5,
            color: FILLS.textMuted,
          }}
        >
          <a
            href={PRIVACY_COOKIES_HREF}
            style={{ color: FILLS.goldWarm, textDecoration: 'underline' }}
          >
            {PREFERENCES_COPY.footnotePolicy}
          </a>
          . {PREFERENCES_COPY.footnoteReopen}
        </p>
      </div>
    </dialog>
  );
};

export default CookiePreferencesPanel;
